"use client";

import { useCallback, useEffect, useRef, useState, type Key, type ReactNode } from "react";
import { useT } from "@/lib/i18n/context";
import { useCatalogLocation } from "@/lib/cms/useCatalogLocation";
import { fetchCatalogPageContent } from "@/lib/cms/catalogContent";
import { catalogLoadMoreTranslations } from "./CatalogLoadMore.i18n";
import styles from "./CatalogLoadMore.module.css";

/**
 * CatalogLoadMore - wspólny standard paginacji "Wczytaj więcej" dla publicznych
 * komponentów katalogu (c_news, c_blog, c_photo_gallery, c_video_gallery_outer).
 *
 * **Hybryda UX + SEO:**
 *   - Przycisk to realny `<a href="/.../pageN">` -> robot Google i tryb bez JS
 *     przechodzą na osobny, indeksowalny URL strony N (SSR renderuje slice tej
 *     strony). Wszystkie wpisy pozostają chodliwe.
 *   - Z JS: klik przechwytywany (`preventDefault`), doczytuje stronę N+1 i
 *     dokleja pod spodem BEZ przeładowania, a URL aktualizowany przez
 *     `history.pushState` (`/.../pageN+1`) - shareable / back-button.
 *   - Wejście bezpośrednio na `/pageN` -> SSR podaje `initialItems` dla tej strony
 *     (tryb serwerowy) -> dalej "Wczytaj więcej" do N+1.
 *
 * **Dwa tryby źródła danych:**
 *   - serwerowy (domyślny): podaj `extractItems` - komponent sam fetch'uje
 *     `/api/cms/catalog-content?page=N` i wyciąga tablicę itemów.
 *   - kliencki: podaj `loadPage` - komponent woła tę funkcję (np. slice tablicy
 *     już w pamięci, dla komponentów z filtrem client-side).
 *
 * Render itemów oddany przez render-prop `children(items)` - każdy komponent
 * zachowuje własny markup listy.
 */
export type CatalogLoadMoreProps<T> = {
  /** Itemy strony początkowej (z SSR dla bieżącego `pageN`). */
  initialItems: T[];
  /** Numer strony początkowej (z SSR / URL). */
  initialPage: number;
  /** Łączna liczba stron (z `pagination.total_pages`). */
  totalPages: number;
  /** Stabilny klucz itemu (dedup przy doklejaniu + React key). */
  getKey: (item: T) => Key;
  /** Render listy z pełnego (doklejanego) zbioru itemów. */
  children: (items: T[]) => ReactNode;
  /** Tryb serwerowy: wyciąga tablicę itemów z payloadu `getApiContent`. */
  extractItems?: (content: unknown) => T[];
  /** Tryb kliencki: zwraca itemy danej strony (np. slice z pamięci). */
  loadPage?: (page: number) => Promise<T[]> | T[];
  /** Opcjonalna łączna liczba itemów - pokazuje "Pokazano X z Y" pod przyciskiem. */
  totalCount?: number;
  /** Czy aktualizować URL (`pushState` na `/pageN`) przy doczytywaniu. Domyślnie
      true. Wyłączane np. gdy aktywny jest filtr client-side (URL strony nie
      odzwierciedlałby przefiltrowanego zbioru). */
  updateUrl?: boolean;
};

export function CatalogLoadMore<T>({
  initialItems,
  initialPage,
  totalPages,
  getKey,
  children,
  extractItems,
  loadPage,
  totalCount,
  updateUrl = true,
}: CatalogLoadMoreProps<T>) {
  const t = useT(catalogLoadMoreTranslations);
  const loc = useCatalogLocation();

  const [items, setItems] = useState<T[]>(initialItems);
  const [page, setPage] = useState<number>(initialPage);
  const [loading, setLoading] = useState(false);

  /* Reset gdy SSR poda nowe dane (nawigacja / back-button -> re-render z innym
     initialItems). Porównujemy referencję - świeży SSR zawsze daje nową tablicę. */
  const initRef = useRef(initialItems);
  useEffect(() => {
    if (initRef.current !== initialItems) {
      initRef.current = initialItems;
      setItems(initialItems);
      setPage(initialPage);
    }
  }, [initialItems, initialPage]);

  const hasMore = page < totalPages;
  const nextHref = loc.buildPageHref(page + 1);

  const handleLoadMore = useCallback(
    async (e?: React.MouseEvent) => {
      if (e) e.preventDefault();
      if (loading || !hasMore) return;

      setLoading(true);
      const next = page + 1;
      try {
        let newItems: T[] = [];
        if (loadPage) {
          newItems = await loadPage(next);
        } else if (extractItems) {
          const content = await fetchCatalogPageContent(loc.slug, loc.locale, {
            page: next,
            articleId: loc.articleId,
          });
          newItems = content ? extractItems(content) : [];
        }

        setItems((prev) => {
          const seen = new Set(prev.map(getKey));
          const merged = [...prev];
          for (const it of newItems) {
            const k = getKey(it);
            if (!seen.has(k)) {
              seen.add(k);
              merged.push(it);
            }
          }
          return merged;
        });
        setPage(next);

        /* Aktualizacja URL bez przeładowania (Next 15: pushState nie re-runuje
           Server Componentu, stan doklejony zostaje). */
        if (updateUrl && typeof window !== "undefined") {
          window.history.pushState(null, "", nextHref);
        }
      } finally {
        setLoading(false);
      }
    },
    [loading, hasMore, page, loadPage, extractItems, loc.slug, loc.locale, loc.articleId, getKey, nextHref, updateUrl],
  );

  return (
    <>
      {children(items)}

      {hasMore && (
        <div className={styles.wrap}>
          {/* realny href dla SEO + no-JS; onClick przechwytuje dla SPA-load */}
          <a
            href={nextHref}
            className={styles.button}
            onClick={handleLoadMore}
            data-loading={loading ? "true" : undefined}
            aria-busy={loading}
            rel="next"
          >
            {loading ? t.loading : t.loadMore}
          </a>
          {typeof totalCount === "number" && (
            <span className={styles.info}>{t.shown(items.length, totalCount)}</span>
          )}
        </div>
      )}
    </>
  );
}
